import { useMemo } from "react";
import QuickAddList from "@/components/steps/accessories/QuickAddList";
import AddedItemsList from "@/components/steps/accessories/AddedItemsList";
import AddAccessoryForms from "@/components/steps/accessories/AddAccessoryForms";

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

export default function Step3Accessories({ accessories, setAccessories, masterAccessories }) {
  // Admin-pinned master accessories appear in the Quick Add strip.
  const quickItems = useMemo(
    () => (masterAccessories || []).filter((m) => m.is_quick_add),
    [masterAccessories],
  );

  // Visible "Added items" — filtered subset of the live cart.
  const addedItems = useMemo(
    () => accessories.filter((a) => (a.quantity || 0) > 0),
    [accessories],
  );

  const findQuick = (name) =>
    accessories.find((a) => a.name === name && !a.custom);

  const setQuickQty = (name, qty) => {
    const existing = findQuick(name);
    const masterItem = (masterAccessories || []).find((m) => m.name === name);
    const item = existing || {
      id: uid(),
      name,
      quantity: 0,
      price_per_unit: masterItem?.price_per_unit || 0,
      custom: false,
    };
    const newItem = { ...item, quantity: Math.max(0, qty) };
    const idx = accessories.findIndex((a) => a.id === item.id);
    const next = idx >= 0
      ? accessories.map((a, i) => (i === idx ? newItem : a))
      : [...accessories, newItem];
    setAccessories(next);
  };

  const addItem = (partial) =>
    setAccessories([...accessories, { id: uid(), ...partial }]);

  const removeItem = (id) =>
    setAccessories(accessories.filter((a) => a.id !== id));

  const updateQty = (id, qty) =>
    setAccessories(
      accessories.map((a) =>
        a.id === id ? { ...a, quantity: parseFloat(qty) || 0 } : a
      )
    );

  const toggleInclude = (id) =>
    setAccessories(
      accessories.map((a) =>
        a.id === id ? { ...a, is_included: a.is_included === false } : a
      )
    );

  return (
    <div data-testid="step-3-accessories">
      <div className="mb-6">
        <div className="overline text-[#C5A880] mb-2">Step 03</div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold leading-tight">
          Accessories
        </h1>
        <p className="text-white/50 mt-2 text-sm">
          Quick-tap common items or add from the master list.
        </p>
      </div>

      <QuickAddList
        quickItems={quickItems}
        findQuick={findQuick}
        setQuickQty={setQuickQty}
      />

      <AddedItemsList
        addedItems={addedItems}
        updateQty={updateQty}
        toggleInclude={toggleInclude}
        removeItem={removeItem}
      />

      <AddAccessoryForms masterAccessories={masterAccessories} onAdd={addItem} />
    </div>
  );
}
